import React, { useEffect, useState } from "react"
import types from "prop-types"
import * as API from "services/api"
import { useCustomName } from "hooks"
import { errorToast } from "shared/toast"
import { groupAudit } from "utils/auditHelper"
import { FACILITY_KEY } from "utils/customNameHelpers"
import FacilityAuditTrailContext from "../FacilityAuditTrailContext"

const auditTrailErrorMessage = (facilityName) => (
  `Something went wrong. Unable to load the ${facilityName}'s audit trail.`
)

const FacilityAuditTrailContextProvider = ({ children, facilityId }) => {
  // For loading the facility's audits
  const [auditList, setAuditList] = useState([])
  const [page, setPage] = useState(1)
  const [isLoading, setIsLoading] = useState(true)
  const [allAuditsLoaded, setAllAuditsLoaded] = useState(false)

  const { nameInSentence: facilityName } = useCustomName(FACILITY_KEY)

  const fetchAudits = async () => {
    if (!facilityId) return
    setIsLoading(true)

    const response = await API.getFacilityAudits({ facilityId, page })
    setIsLoading(false)

    if (response.ok) {
      if (response.data.length === 0) {
        setAllAuditsLoaded(true)
      } else {
        setPage(page + 1)
        setAuditList(auditList.concat(response.data))
      }
    } else {
      errorToast(auditTrailErrorMessage(facilityName))
    }
  }

  // If the facility id changes, we need to reset the state of the component,
  // fetching the first page of audits for the facility.
  useEffect(() => {
    const initializeStateForFacility = async () => {
      const auditsResponse = await API.getFacilityAudits({ facilityId, page: 1 })
      setIsLoading(false)

      if (auditsResponse.ok) {
        setAllAuditsLoaded(auditsResponse.data.length === 0)
        setAuditList(auditsResponse.data)
        setPage(2)
      } else {
        errorToast(auditTrailErrorMessage(facilityName))
      }
    }

    initializeStateForFacility()
  }, [facilityId, facilityName])

  const contextConsumerValue = {
    allAuditsLoaded,
    auditGroups: groupAudit(auditList),
    fetchAudits,
    isLoading,
    page,
  }

  return (
    <FacilityAuditTrailContext.Provider value={contextConsumerValue}>
      {children}
    </FacilityAuditTrailContext.Provider>
  )
}

FacilityAuditTrailContextProvider.propTypes = {
  children: types.node.isRequired,
  facilityId: types.number.isRequired,
}

export default FacilityAuditTrailContextProvider
